import { Field } from '../../inventaires/shared/types';
import { fieldsWoodarea, type WoodareaField } from '@repo/field-configs';

/**
 * Maps WoodareaField to Field type for use with FormField component
 */
export function mapWoodareaFieldToField(fieldName: string): Field | null {
  const woodareaField = fieldsWoodarea[fieldName as keyof typeof fieldsWoodarea] as WoodareaField | undefined;

  if (!woodareaField) {
    return null;
  }

  const field: Field = {
    name: fieldName,
    label: woodareaField.label || fieldName,
    type: getFieldType(woodareaField),
    required: woodareaField.required || false,
    options: woodareaField.values?.map(v => ({ id: v.id, label: v.label })) || [],
    placeholder: undefined,
  };

  // Add unit if postFieldLabel exists
  if (woodareaField.postFieldLabel) {
    field.unit = woodareaField.postFieldLabel;
  }

  return field;
}

/**
 * Gets the Field type based on WoodareaField widget and type
 */
function getFieldType(woodareaField: WoodareaField): Field['type'] {
  // If it's a scalar with Text widget, determine if it's number or text
  if (woodareaField.type === 'scalaire' && woodareaField.widget === 'Text') {
    // Check if the field name suggests it's a number field
    const numberFields = ['diametre', 'hauteur', 'countSubject', 'nbreSujetConcerne'];
    const fieldName = woodareaField.server || woodareaField.name;
    if (numberFields.some(nf => fieldName.toLowerCase().includes(nf.toLowerCase()))) {
      return 'number';
    }
    return 'text';
  }

  // If it's a scalar with List widget, it's a select (single choice)
  if (woodareaField.type === 'scalaire' && woodareaField.widget === 'List') {
    return 'radio';
  }

  // If it's multiple with List widget, it's a multiselect
  if (woodareaField.type === 'multiple' && woodareaField.widget === 'List') {
    return 'multiselect';
  }

  // Default to text
  return 'text';
}

/**
 * Gets the server field name from woodarea field name
 */
export function getServerFieldName(fieldName: string): string {
  const woodareaField = fieldsWoodarea[fieldName as keyof typeof fieldsWoodarea] as WoodareaField | undefined;
  return woodareaField?.server || fieldName;
}

/**
 * Maps form field name to woodarea field name
 */
export function getWoodareaFieldName(formFieldName: string): string | null {
  // Find the woodarea field that matches the server name
  for (const [key, woodareaField] of Object.entries(fieldsWoodarea)) {
    if (woodareaField.server === formFieldName) {
      return key;
    }
  }
  return null;
}

/**
 * Gets all woodarea field names for a given category
 */
export function getWoodareaFieldsByCategory(category: number): string[] {
  return Object.entries(fieldsWoodarea)
    .filter(([_, field]) => field.category === category)
    .map(([key, _]) => key);
}

/**
 * Gets woodarea field configuration by field name
 */
export function getWoodareaFieldConfig(fieldName: string): WoodareaField | null {
  return (fieldsWoodarea[fieldName as keyof typeof fieldsWoodarea] as WoodareaField) || null;
}

